import { type FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { useSetRoadmapStatus } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

import { Field } from "./item-form";
import { StepButtons } from "./step-buttons";
import { parseWholeHours } from "./utils";

interface FinishFormProps {
  itemId: string;
  /** Back without a change: Annuleren. */
  onCancel: () => void;
  /** After the item is done. */
  onFinished: () => void;
}

/**
 * Markeer klaar: the whole hours the item took, or none to count its
 * estimate, then the move to done. Enter in the box saves too. A failed save
 * says why, and the form stays. In an item's details and in the status
 * menu's small dialog.
 */
export function FinishForm({ itemId, onCancel, onFinished }: FinishFormProps) {
  const { t } = useTranslation();
  const id = useId();
  const status = useSetRoadmapStatus();
  const [text, setText] = useState("");
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const hours = parseWholeHours(text);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (hours === undefined || status.isPending) {
      return;
    }
    setFailure(null);
    try {
      await status.mutateAsync({
        itemId,
        change: hours === null ? { status: "done" } : { status: "done", actual_hours: hours },
      });
      onFinished();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-3" data-section="finish">
      <Field
        label={t("roadmap.details.finishActual")}
        htmlFor={id}
        hint={hours === undefined ? undefined : t("roadmap.details.finishHint")}
      >
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          value={text}
          onChange={(event) => setText(event.target.value)}
          aria-invalid={hours === undefined || undefined}
          className="h-9 w-32 tabular-nums pointer-coarse:h-10"
          autoFocus
        />
      </Field>
      {hours === undefined && (
        <p className="text-destructive text-xs">{t("roadmap.developer.actualInvalid")}</p>
      )}
      {failure && (
        <p role="alert" className="text-destructive text-sm">
          {apiErrorMessage(failure.error, "setRoadmapStatus")}
        </p>
      )}
      <StepButtons
        onBack={onCancel}
        confirm={t("roadmap.details.finish")}
        pending={status.isPending}
        disabled={hours === undefined}
      />
    </form>
  );
}
